import { useMemo, useState } from "react";
import {
  AlertTriangle,
  Ban,
  Check,
  CircleCheck,
  CornerDownRight,
  Eye,
  EyeOff,
  Heading,
  LoaderCircle,
  MoreHorizontal,
  RotateCcw,
  Sparkles,
} from "lucide-react";
import { applyImportConfig } from "@/lib/mapper/import-transform";
import type { ImportConfig } from "@/lib/mapper/types";
import type { PreviewSheet } from "@/lib/api/import";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A saved template the user can bind an imported sheet to. */
export type ImportTemplateOption = { id: string; name: string };

/** The user's explicit picks on the import screen. */
export type ImportPicks = {
  /** Sheets to import, in workbook order. */
  sheets: string[];
  /** Sheet → template id, or null for "no template". */
  templates: Record<string, string | null>;
};

export type HeaderConfiguratorProps = {
  sheets: PreviewSheet[];
  initialConfigs?: Record<string, ImportConfig>;
  mode?: "create" | "reconfigure";
  confirmLabel?: string;
  busy?: boolean;
  /**
   * When given, the user picks which sheets to import (a checkbox per sheet)
   * and a template for each one, and `onConfirm` receives those picks. Nothing
   * is pre-selected from the sheet's contents.
   */
  templates?: ImportTemplateOption[];
  onConfirm: (configs: Record<string, ImportConfig>, picks?: ImportPicks) => void;
  onCancel?: () => void;
};

type HeroState = "prompt" | "confirmed" | "adjusting";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function defaultConfig(): ImportConfig {
  return {
    headerRowStart: 0,
    headerRowEnd: 0,
    dataStartIdx: 1,
    skippedRowIdxs: [],
    excludedColIdxs: [],
    headerOverrides: {},
    joinSeparator: " / ",
  };
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function colLetter(idx: number): string {
  let result = "";
  let n = idx;
  do {
    result = String.fromCharCode(65 + (n % 26)) + result;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return result;
}

function cellString(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) return value.toLocaleDateString();
  return String(value);
}

function confidencePct(c: number): number {
  return Math.round(c * 100);
}

// ---------------------------------------------------------------------------
// Stepper — matching ImportWizard's NumberStepper but demo-style display
// ---------------------------------------------------------------------------

type StepperProps = {
  label: string;
  value: number; // 0-based internal value
  min: number;
  max: number;
  onChange: (n: number) => void;
  disabled?: boolean;
};

function InlineStepper({ label, value, min, max, onChange, disabled }: StepperProps) {
  return (
    <label className="flex items-center gap-2">
      <span className="text-[12px] font-medium text-slate-500 dark:text-slate-400">{label}</span>
      <span className="inline-flex items-center overflow-hidden rounded-lg border border-slate-300 bg-white dark:border-border dark:bg-surface">
        <button
          type="button"
          disabled={disabled || value <= min}
          onClick={() => onChange(clamp(value - 1, min, max))}
          aria-label={`Decrease ${label}`}
          className="px-2 py-1 text-[14px] text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:text-slate-300 dark:hover:bg-surface-2"
        >
          −
        </button>
        <span className="min-w-7 border-x border-slate-200 px-1 py-1 text-center font-mono text-[12px] font-semibold text-slate-900 dark:border-border dark:text-slate-100">
          {value + 1}
        </span>
        <button
          type="button"
          disabled={disabled || value >= max}
          onClick={() => onChange(clamp(value + 1, min, max))}
          aria-label={`Increase ${label}`}
          className="px-2 py-1 text-[14px] text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:text-slate-300 dark:hover:bg-surface-2"
        >
          +
        </button>
      </span>
    </label>
  );
}

// ---------------------------------------------------------------------------
// RowMenu — per-row ⋯ context menu
// ---------------------------------------------------------------------------

type RowRole = "header" | "skipped" | "ignored" | "data";

function RowMenu({
  role,
  onHeader,
  onData,
  onSkip,
  onClose,
}: {
  role: RowRole;
  onHeader: () => void;
  onData: () => void;
  onSkip: () => void;
  onClose: () => void;
}) {
  return (
    <>
      <button className="fixed inset-0 z-20 cursor-default" aria-hidden onClick={onClose} />
      <div className="absolute left-1/2 top-[calc(100%+4px)] z-30 w-44 -translate-x-1/2 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 text-left shadow-lg dark:border-border dark:bg-surface">
        <RowMenuItem
          icon={<Heading size={14} />}
          label="Set as header"
          onClick={onHeader}
          active={role === "header"}
        />
        <RowMenuItem
          icon={<CornerDownRight size={14} />}
          label="Data starts here"
          onClick={onData}
        />
        <RowMenuItem
          icon={<Ban size={14} />}
          label={role === "skipped" ? "Un-skip row" : "Skip this row"}
          onClick={onSkip}
        />
      </div>
    </>
  );
}

function RowMenuItem({
  icon,
  label,
  onClick,
  active,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2.5 px-3 py-1.5 text-[12.5px] transition hover:bg-slate-50 dark:hover:bg-surface-2"
      style={{ color: active ? undefined : undefined }}
    >
      <span className="text-slate-400 dark:text-slate-500">{icon}</span>
      <span className={active ? "font-semibold text-blue-600 dark:text-blue-400" : "text-slate-700 dark:text-slate-200"}>
        {label}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Legend dot
// ---------------------------------------------------------------------------

function Legend({ className, label }: { className: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className={`h-2.5 w-2.5 rounded-sm ${className}`} />
      <span className="text-slate-400 dark:text-slate-500">{label}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Live "What will import" preview
// ---------------------------------------------------------------------------

function WhatWillImport({ sheet, config }: { sheet: PreviewSheet; config: ImportConfig }) {
  const { headers, rows } = useMemo(
    () => applyImportConfig(sheet.rawGrid, sheet.merges, config),
    [sheet, config],
  );

  if (headers.length === 0) {
    return (
      <div className="rounded-md border border-slate-200 px-3 py-3 text-[13px] text-slate-500 dark:border-border dark:text-slate-400">
        No columns will be imported with the current configuration.
      </div>
    );
  }

  const previewRows = rows.slice(0, 5);

  return (
    <section className="overflow-hidden rounded-[10px] border border-slate-200 bg-white shadow-sm dark:border-border dark:bg-surface">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5 dark:border-border">
        <span className="flex items-center gap-2 text-[13px] font-medium text-slate-900 dark:text-slate-100">
          <CircleCheck size={15} className="text-green-600 dark:text-green-400" />
          What will import
        </span>
        <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-600 dark:bg-surface-2 dark:text-slate-300">
          {headers.length} column{headers.length !== 1 ? "s" : ""} · {rows.length} row{rows.length !== 1 ? "s" : ""}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[12px]" aria-label="Live import preview">
          <thead>
            <tr>
              {headers.map((h) => (
                <th
                  key={h}
                  scope="col"
                  className="border-b border-r border-blue-200 bg-blue-50 px-2.5 py-2 text-left font-semibold text-blue-900 last:border-r-0 dark:border-blue-900/30 dark:bg-blue-950/20 dark:text-blue-100"
                  style={{ minWidth: 120 }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {previewRows.map((row, i) => (
              <tr key={i} className="even:bg-slate-50/60 dark:even:bg-surface-2/40">
                {headers.map((h) => (
                  <td
                    key={h}
                    className="truncate border-b border-r border-slate-200 px-2.5 py-1.5 text-slate-700 last:border-r-0 dark:border-border dark:text-slate-300"
                    style={{ maxWidth: 200 }}
                  >
                    {cellString(row[h] ?? null)}
                  </td>
                ))}
              </tr>
            ))}
            {previewRows.length === 0 && (
              <tr>
                <td
                  colSpan={headers.length}
                  className="px-2.5 py-3 text-center text-slate-400 dark:text-slate-500"
                >
                  No data rows in the selected range.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {rows.length > 5 && (
          <p className="border-t border-slate-200 px-4 py-1.5 text-[11px] text-slate-500 dark:border-border dark:text-slate-400">
            + {rows.length - 5} more rows
          </p>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Per-sheet configurator panel
// ---------------------------------------------------------------------------

type SheetConfigPanelProps = {
  sheet: PreviewSheet;
  config: ImportConfig;
  lowConfidence: boolean;
  onConfig: (next: ImportConfig) => void;
  onConfirmed: () => void;
};

function SheetConfigPanel({
  sheet,
  config,
  lowConfidence,
  onConfig,
  onConfirmed,
}: SheetConfigPanelProps) {
  const maxRow = Math.max(0, sheet.rawGrid.length - 1);
  const headerSpan = config.headerRowEnd - config.headerRowStart + 1;
  const [hero, setHero] = useState<HeroState>(lowConfidence ? "adjusting" : "prompt");
  const [openMenu, setOpenMenu] = useState<number | null>(null);

  // Derived column count from rawGrid
  const colCount = useMemo(() => {
    let max = 0;
    for (const row of sheet.rawGrid) {
      if (Array.isArray(row) && row.length > max) max = row.length;
    }
    return max;
  }, [sheet.rawGrid]);

  const excludedSet = useMemo(
    () => new Set(config.excludedColIdxs),
    [config.excludedColIdxs],
  );
  const skippedSet = useMemo(
    () => new Set(config.skippedRowIdxs),
    [config.skippedRowIdxs],
  );

  const headerRowsSet = useMemo(() => {
    const s = new Set<number>();
    for (let i = 0; i <= config.headerRowEnd - config.headerRowStart; i++) {
      s.add(config.headerRowStart + i);
    }
    return s;
  }, [config.headerRowStart, config.headerRowEnd]);

  function roleOf(idx: number): RowRole {
    if (headerRowsSet.has(idx)) return "header";
    if (skippedSet.has(idx)) return "skipped";
    if (idx < config.dataStartIdx) return "ignored";
    return "data";
  }

  function updateHeaderRange(start: number, end: number) {
    const newStart = clamp(start, 0, maxRow);
    const newEnd = clamp(end, newStart, maxRow);
    const newData = clamp(config.dataStartIdx, newEnd + 1, maxRow + 1);
    onConfig({
      ...config,
      headerRowStart: newStart,
      headerRowEnd: newEnd,
      dataStartIdx: newData,
      skippedRowIdxs: config.skippedRowIdxs.filter((i) => i < newStart || i > newEnd),
    });
  }

  function setSpan(span: 1 | 2) {
    updateHeaderRange(config.headerRowStart, config.headerRowStart + span - 1);
  }

  function setAsHeader(idx: number) {
    updateHeaderRange(idx, idx + headerSpan - 1);
    setOpenMenu(null);
  }

  function startDataHere(idx: number) {
    const newData = idx;
    const newStart = Math.min(config.headerRowStart, Math.max(0, newData - 1));
    const newEnd = Math.min(config.headerRowEnd, newStart);
    onConfig({
      ...config,
      headerRowStart: newStart,
      headerRowEnd: newEnd,
      dataStartIdx: newData,
      skippedRowIdxs: config.skippedRowIdxs.filter((i) => i !== idx),
    });
    setOpenMenu(null);
  }

  function toggleSkip(idx: number) {
    const next = new Set(skippedSet);
    if (next.has(idx)) {
      next.delete(idx);
    } else {
      next.add(idx);
    }
    onConfig({ ...config, skippedRowIdxs: Array.from(next).sort((a, b) => a - b) });
    setOpenMenu(null);
  }

  function toggleCol(c: number) {
    const next = new Set(excludedSet);
    if (next.has(c)) {
      next.delete(c);
    } else {
      next.add(c);
    }
    onConfig({ ...config, excludedColIdxs: Array.from(next).sort((a, b) => a - b) });
  }

  function reDetect() {
    import("@/lib/mapper/import-transform")
      .then(({ detectImportConfig }) => {
        const { config: suggested } = detectImportConfig(sheet.rawGrid, sheet.merges);
        onConfig(suggested);
        setHero("prompt");
      })
      .catch(() => {/* ignore */});
  }

  function handleConfirm() {
    setHero("confirmed");
    onConfirmed();
  }

  // Derived header labels (read from rawGrid, respecting headerOverrides)
  const headerLabels = useMemo(() => {
    return Array.from({ length: colCount }, (_, c) => {
      if (config.headerOverrides[c] !== undefined && config.headerOverrides[c] !== "") {
        return config.headerOverrides[c];
      }
      const parts: string[] = [];
      for (let i = 0; i <= config.headerRowEnd - config.headerRowStart; i++) {
        const t = cellString(sheet.rawGrid[config.headerRowStart + i]?.[c]).trim();
        if (t) parts.push(t);
      }
      return parts.length ? parts.join(config.joinSeparator || " / ") : `Column ${colLetter(c)}`;
    });
  }, [sheet.rawGrid, config, colCount]);

  const skipCount = skippedSet.size;
  const confidence = sheet.confidence;

  return (
    <div className="space-y-5">
      {/* Confidence hero */}
      {hero === "prompt" && (
        <div className="flex flex-col gap-4 rounded-[10px] border border-blue-200 bg-blue-50 p-5 shadow-sm dark:border-blue-900/40 dark:bg-blue-950/20 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
              <Sparkles size={19} />
            </span>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-[15px] font-semibold text-slate-900 dark:text-slate-100">
                  Headers found on row {config.headerRowStart + 1}
                </h2>
                <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-700 dark:bg-green-950/30 dark:text-green-400">
                  {confidencePct(confidence)}% sure
                </span>
              </div>
              <p className="mt-1 text-[13px] leading-5 text-slate-600 dark:text-slate-400">
                Data starts on row {config.dataStartIdx + 1}.
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => setHero("adjusting")}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-[13px] font-medium text-slate-700 transition hover:bg-slate-50 dark:border-border dark:bg-surface dark:text-slate-200 dark:hover:bg-surface-2"
            >
              I&apos;ll adjust
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-[13px] font-medium text-white transition hover:bg-slate-700 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
            >
              <Check size={15} /> Looks right
            </button>
          </div>
        </div>
      )}

      {hero === "confirmed" && (
        <div className="flex items-center justify-between gap-3 rounded-[10px] border border-transparent bg-green-50 px-4 py-3 dark:bg-green-950/30">
          <span className="flex items-center gap-2 text-[13px] font-medium text-green-700 dark:text-green-400">
            <Check size={16} />
            Header confirmed on row {config.headerRowStart + 1} · data from row {config.dataStartIdx + 1}
          </span>
          <button
            type="button"
            onClick={() => setHero("adjusting")}
            className="text-[12px] font-medium text-green-700 underline-offset-2 hover:underline dark:text-green-400"
          >
            Change
          </button>
        </div>
      )}

      {hero === "adjusting" && (
        <div className="flex items-center justify-between gap-3 rounded-[10px] border border-slate-200 bg-slate-50 px-4 py-3 dark:border-border dark:bg-surface-2">
          <span className="text-[13px] text-slate-600 dark:text-slate-400">
            Hover any row for options. Set the{" "}
            <strong className="text-slate-900 dark:text-slate-100">header</strong> and where{" "}
            <strong className="text-slate-900 dark:text-slate-100">data starts</strong>.
          </span>
          <button
            type="button"
            onClick={handleConfirm}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-[12px] font-medium text-white transition hover:bg-slate-700 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
          >
            <Check size={13} /> Looks good
          </button>
        </div>
      )}

      {/* Low-confidence banner (overrides hero in adjusting state) */}
      {lowConfidence && hero === "adjusting" && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <strong>Couldn&apos;t auto-detect the header.</strong> Please set the header row using the controls below or by hovering rows in the grid.
          </span>
        </div>
      )}

      {/* Control strip */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <InlineStepper
          label="Header row"
          value={config.headerRowStart}
          min={0}
          max={maxRow}
          onChange={(n) => updateHeaderRange(n, n + headerSpan - 1)}
        />

        {/* Header span — demo-style segmented control */}
        <label className="flex items-center gap-2">
          <span className="text-[12px] font-medium text-slate-500 dark:text-slate-400">Header spans</span>
          <span className="inline-flex gap-0.5 rounded-lg border border-slate-200 bg-slate-100 p-0.5 dark:border-border dark:bg-surface-2">
            {([1, 2] as const).map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setSpan(n)}
                className={[
                  "rounded-md px-3 py-1 text-[12px] font-medium transition",
                  headerSpan === n
                    ? "bg-white text-slate-900 shadow-sm dark:bg-surface dark:text-slate-100"
                    : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200",
                ].join(" ")}
              >
                {n} row{n > 1 ? "s" : ""}
              </button>
            ))}
          </span>
        </label>

        <InlineStepper
          label="Data starts at row"
          value={config.dataStartIdx}
          min={config.headerRowEnd + 1}
          max={maxRow + 1}
          onChange={(n) => onConfig({ ...config, dataStartIdx: clamp(n, config.headerRowEnd + 1, maxRow + 1) })}
        />

        {/* Join separator (visible when spanning 2 rows) */}
        {headerSpan > 1 && (
          <label className="flex items-center gap-2">
            <span className="text-[12px] font-medium text-slate-500 dark:text-slate-400">Join separator</span>
            <input
              type="text"
              value={config.joinSeparator}
              onChange={(e) => onConfig({ ...config, joinSeparator: e.currentTarget.value })}
              aria-label="Header join separator"
              className="w-16 rounded border border-slate-300 bg-white px-2 py-0.5 text-[12px] text-slate-800 outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-200 dark:border-border dark:bg-surface dark:text-slate-100 dark:focus:ring-slate-700"
            />
          </label>
        )}

        <div className="ml-auto flex items-center gap-2">
          {skipCount > 0 && (
            <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-medium text-amber-700 dark:bg-amber-950/30 dark:text-amber-400">
              {skipCount} junk row{skipCount > 1 ? "s" : ""} skipped
            </span>
          )}
          <button
            type="button"
            onClick={reDetect}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-600 transition hover:bg-slate-50 dark:border-border dark:bg-surface dark:text-slate-300 dark:hover:bg-surface-2"
          >
            <RotateCcw size={13} /> Re-detect
          </button>
        </div>
      </div>

      {/* Raw grid */}
      <section className="overflow-hidden rounded-[10px] border border-slate-200 bg-white shadow-sm dark:border-border dark:bg-surface">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5 dark:border-border">
          <span className="text-[13px] font-medium text-slate-900 dark:text-slate-100">
            {sheet.name}
            <span className="ml-2 text-[12px] text-slate-400 dark:text-slate-500">
              raw file
            </span>
          </span>
          <span className="hidden items-center gap-3 text-[11px] sm:flex">
            <Legend className="bg-blue-200 dark:bg-blue-900/50" label="header" />
            <Legend className="bg-amber-200 dark:bg-amber-900/50" label="skipped" />
            <Legend className="bg-slate-200 dark:bg-slate-700" label="ignored" />
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[12.5px]">
            <thead>
              <tr>
                {/* Row number gutter header */}
                <th className="sticky left-0 z-10 w-10 border-b border-r border-slate-200 bg-slate-50 px-0 py-2 text-center align-bottom dark:border-border dark:bg-surface-2">
                  <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                    #
                  </span>
                </th>
                {/* Column headers with eye toggle and editable label */}
                {Array.from({ length: colCount }, (_, c) => {
                  const excluded = excludedSet.has(c);
                  return (
                    <th
                      key={c}
                      className="border-b border-r border-slate-200 bg-slate-50 px-1.5 py-1 align-bottom dark:border-border dark:bg-surface-2"
                      style={{ minWidth: 100, opacity: excluded ? 0.45 : 1 }}
                    >
                      <div className="flex items-center justify-between gap-0.5">
                        <span className="font-mono text-[9px] leading-none text-slate-400 dark:text-slate-500">
                          {colLetter(c)}
                        </span>
                        <button
                          type="button"
                          onClick={() => toggleCol(c)}
                          title={excluded ? "Include column" : "Exclude column"}
                          className="flex h-4 w-4 items-center justify-center rounded text-slate-400 transition hover:bg-slate-100 dark:text-slate-500 dark:hover:bg-surface"
                        >
                          {excluded ? <EyeOff size={11} /> : <Eye size={11} />}
                        </button>
                      </div>
                      {/* Derived label — click to rename */}
                      <EditableLabel
                        label={excluded ? "— excluded —" : headerLabels[c]}
                        override={config.headerOverrides[c]}
                        onEdit={(val) => {
                          const next = { ...config.headerOverrides };
                          if (val === "") {
                            delete next[c];
                          } else {
                            next[c] = val;
                          }
                          onConfig({ ...config, headerOverrides: next });
                        }}
                        dim={excluded}
                      />
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sheet.rawGrid.slice(0, 10).map((row, idx) => {
                const role = roleOf(idx);
                const rowBg =
                  role === "header"
                    ? "bg-blue-50 dark:bg-blue-950/20"
                    : role === "skipped"
                      ? "bg-amber-50 dark:bg-amber-950/20"
                      : "";
                const dim = role === "ignored" || role === "skipped";
                const isHeader = role === "header";
                return (
                  <tr key={idx} className={`group/row ${rowBg}`}>
                    {/* Row gutter */}
                    <td
                      className={[
                        "sticky left-0 z-10 border-b border-r border-slate-200 p-0 text-center align-middle dark:border-border",
                        role === "header"
                          ? "bg-blue-50 dark:bg-blue-950/20"
                          : role === "skipped"
                            ? "bg-amber-50 dark:bg-amber-950/20"
                            : "bg-slate-50 dark:bg-surface-2",
                      ].join(" ")}
                      style={{
                        borderLeft: role === "header" ? "2px solid rgb(147 197 253)" : "2px solid transparent",
                      }}
                    >
                      <div className="relative flex h-8 items-center justify-center gap-1">
                        <span
                          className={[
                            "font-mono text-[11px]",
                            role === "header" ? "text-blue-600 dark:text-blue-400" : "text-slate-400 dark:text-slate-500",
                          ].join(" ")}
                        >
                          {idx + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => setOpenMenu(openMenu === idx ? null : idx)}
                          className="flex h-5 w-5 items-center justify-center rounded text-slate-400 opacity-0 transition group-hover/row:opacity-100 dark:text-slate-500"
                          aria-label={`Row ${idx + 1} options`}
                        >
                          <MoreHorizontal size={14} />
                        </button>
                        {openMenu === idx && (
                          <RowMenu
                            role={role}
                            onHeader={() => setAsHeader(idx)}
                            onData={() => startDataHere(idx)}
                            onSkip={() => toggleSkip(idx)}
                            onClose={() => setOpenMenu(null)}
                          />
                        )}
                      </div>
                    </td>
                    {/* Cells */}
                    {Array.from({ length: colCount }, (_, c) => {
                      const excluded = excludedSet.has(c);
                      return (
                        <td
                          key={c}
                          className="truncate border-b border-r border-slate-200 px-2 py-1.5 dark:border-border"
                          style={{
                            maxWidth: 200,
                            color: isHeader ? undefined : undefined,
                            fontWeight: isHeader ? 600 : 400,
                            opacity: excluded ? 0.4 : dim ? 0.55 : 1,
                            textDecoration: role === "skipped" ? "line-through" : "none",
                          }}
                        >
                          <span className={isHeader ? "text-slate-900 dark:text-slate-100" : "text-slate-600 dark:text-slate-400"}>
                            {cellString((row as unknown[])?.[c]) || (
                              <span className="text-slate-300 dark:text-slate-600">·</span>
                            )}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {sheet.rawGrid.length > 10 && (
          <p className="border-t border-slate-200 px-4 py-1.5 text-[11px] text-slate-400 dark:border-border dark:text-slate-500">
            Showing first 10 of {sheet.rawGrid.length} rows — use the steppers above to set a header or data row beyond these.
          </p>
        )}
      </section>

      {/* Skipped row summary */}
      {config.skippedRowIdxs.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400">
          <span>Skipped rows:</span>
          {config.skippedRowIdxs.map((idx) => (
            <button
              key={idx}
              type="button"
              onClick={() =>
                onConfig({ ...config, skippedRowIdxs: config.skippedRowIdxs.filter((i) => i !== idx) })
              }
              title={`Un-skip row ${idx + 1}`}
              className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-600 hover:bg-red-50 hover:text-red-600 dark:bg-surface-2 dark:text-slate-300 dark:hover:bg-red-950/30 dark:hover:text-red-400"
            >
              {idx + 1} ✕
            </button>
          ))}
        </div>
      )}

      {/* Excluded column summary */}
      {config.excludedColIdxs.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400">
          <span>Excluded columns:</span>
          {config.excludedColIdxs.map((idx) => (
            <button
              key={idx}
              type="button"
              onClick={() =>
                onConfig({ ...config, excludedColIdxs: config.excludedColIdxs.filter((c) => c !== idx) })
              }
              title={`Include column ${colLetter(idx)}`}
              className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-600 hover:bg-red-50 hover:text-red-600 dark:bg-surface-2 dark:text-slate-300 dark:hover:bg-red-950/30 dark:hover:text-red-400"
            >
              {colLetter(idx)} ✕
            </button>
          ))}
        </div>
      )}

      {/* What will import */}
      <WhatWillImport sheet={sheet} config={config} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline column-label editor
// ---------------------------------------------------------------------------

function EditableLabel({
  label,
  override,
  onEdit,
  dim,
}: {
  label: string;
  override?: string;
  onEdit: (val: string) => void;
  dim: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  function startEdit() {
    setDraft(override ?? "");
    setEditing(true);
  }
  function commit() {
    onEdit(draft.trim());
    setEditing(false);
  }

  if (editing) {
    return (
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.currentTarget.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") setEditing(false);
        }}
        className="mt-0 w-full rounded border border-blue-300 bg-white px-1 py-0 text-[11px] font-semibold text-slate-900 outline-none focus:ring-1 focus:ring-blue-300 dark:border-blue-700 dark:bg-surface dark:text-slate-100"
      />
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      title="Click to rename"
      onClick={startEdit}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") startEdit(); }}
      className={[
        "mt-0 truncate text-left text-[11px] font-semibold leading-tight",
        "cursor-text rounded px-0.5 transition hover:bg-blue-50 hover:ring-1 hover:ring-blue-200 dark:hover:bg-blue-950/30",
        dim ? "text-slate-400 dark:text-slate-500" : "text-slate-900 dark:text-slate-100",
        override ? "italic" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {label}
    </div>
  );
}

// ---------------------------------------------------------------------------
// HeaderConfigurator — main export
// ---------------------------------------------------------------------------

export function HeaderConfigurator({
  sheets,
  initialConfigs,
  mode = "create",
  confirmLabel,
  busy = false,
  templates,
  onConfirm,
  onCancel,
}: HeaderConfiguratorProps) {
  const [activeSheetIdx, setActiveSheetIdx] = useState(0);
  const pickMode = templates !== undefined;
  // Pick mode: only the first sheet starts ticked; every other sheet is the
  // user's explicit choice.
  const [included, setIncluded] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(sheets.map((sheet, i) => [sheet.name, !pickMode || i === 0])),
  );
  const [templateBySheet, setTemplateBySheet] = useState<Record<string, string>>({});
  const isIncluded = (name: string) => included[name] !== false;
  const includedSheets = sheets.filter((sheet) => isIncluded(sheet.name));

  // Per-sheet config state — seeded from suggestedConfig or initialConfigs
  const [configs, setConfigs] = useState<Record<string, ImportConfig>>(() => {
    const seed: Record<string, ImportConfig> = {};
    for (const sheet of sheets) {
      if (initialConfigs?.[sheet.name]) {
        seed[sheet.name] = initialConfigs[sheet.name];
      } else {
        seed[sheet.name] = sheet.suggestedConfig ?? defaultConfig();
      }
    }
    return seed;
  });

  // Low-confidence map — true until the user interacts or clicks confirm
  const [lowConfidence, setLowConfidence] = useState<Record<string, boolean>>(() => {
    const seed: Record<string, boolean> = {};
    for (const sheet of sheets) {
      // In reconfigure mode with an initialConfig, start confirmed
      if (mode === "reconfigure" && initialConfigs?.[sheet.name]) {
        seed[sheet.name] = false;
      } else {
        seed[sheet.name] = sheet.confidence < 0.6;
      }
    }
    return seed;
  });

  const activeSheet = sheets[activeSheetIdx];
  const currentConfig = activeSheet ? (configs[activeSheet.name] ?? defaultConfig()) : null;

  function handleSheetConfig(sheetName: string, next: ImportConfig) {
    setConfigs((prev) => ({ ...prev, [sheetName]: next }));
    // Clear low-confidence once user interacts
    if (lowConfidence[sheetName]) {
      setLowConfidence((prev) => ({ ...prev, [sheetName]: false }));
    }
  }

  function handleSheetConfirmed(sheetName: string) {
    setLowConfidence((prev) => ({ ...prev, [sheetName]: false }));
  }

  const allSheetsReady = includedSheets.every((s) => !lowConfidence[s.name]);

  const defaultLabel = mode === "reconfigure" ? "Apply" : "Confirm";
  const label = confirmLabel ?? defaultLabel;

  // Sheet selector: row of cards (≤3, or always when picking sheets) or
  // <select> (≥4), hidden if only 1 sheet
  const showSheetSelector = sheets.length > 1;
  const sheetSelectorVariant: "cards" | "select" =
    pickMode || sheets.length <= 3 ? "cards" : "select";

  function confirm() {
    if (!pickMode) {
      onConfirm(configs);
      return;
    }
    const names = includedSheets.map((sheet) => sheet.name);
    onConfirm(
      Object.fromEntries(names.map((name) => [name, configs[name] ?? defaultConfig()])),
      {
        sheets: names,
        templates: Object.fromEntries(names.map((name) => [name, templateBySheet[name] || null])),
      },
    );
  }

  // Row count for each sheet using applyImportConfig
  const sheetRowCounts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const sheet of sheets) {
      const cfg = configs[sheet.name] ?? defaultConfig();
      try {
        map[sheet.name] = applyImportConfig(sheet.rawGrid, sheet.merges, cfg).rows.length;
      } catch {
        map[sheet.name] = 0;
      }
    }
    return map;
  }, [sheets, configs]);

  return (
    <div className="flex flex-col gap-6">
      {/* Sheet selector */}
      {showSheetSelector && (
        <div>
          <p className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
            {pickMode ? "Choose the sheets to import" : "Choose which sheet to process"}
          </p>
          {sheetSelectorVariant === "cards" ? (
            <div className="flex flex-wrap gap-2">
              {sheets.map((sheet, i) => {
                const active = i === activeSheetIdx;
                const hasWarning = lowConfidence[sheet.name] && isIncluded(sheet.name);
                return (
                  <div
                    key={sheet.name}
                    className={[
                      "flex min-w-[120px] flex-col rounded-lg border px-3 py-2.5 text-left transition",
                      active
                        ? "border-blue-300 bg-blue-50 shadow-sm dark:border-blue-700 dark:bg-blue-950/30"
                        : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 dark:border-border dark:bg-surface dark:hover:bg-surface-2",
                      pickMode && !isIncluded(sheet.name) ? "opacity-70" : "",
                    ].join(" ")}
                  >
                    <button
                      type="button"
                      onClick={() => setActiveSheetIdx(i)}
                      className="flex flex-col text-left"
                      title={`Show ${sheet.name}`}
                    >
                      <span className={[
                        "flex items-center gap-1 text-[13px] font-semibold",
                        active ? "text-blue-700 dark:text-blue-300" : "text-slate-900 dark:text-slate-100",
                      ].join(" ")}>
                        {sheet.name}
                        {hasWarning && (
                          <span className="text-amber-500 dark:text-amber-400" title="Header not confirmed">⚠</span>
                        )}
                      </span>
                      <span className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                        {sheetRowCounts[sheet.name] ?? 0} rows
                      </span>
                    </button>
                    {pickMode && (
                      <label className="mt-1.5 flex cursor-pointer items-center gap-1.5 text-[11.5px] font-medium text-slate-700 dark:text-slate-300">
                        <input
                          type="checkbox"
                          checked={isIncluded(sheet.name)}
                          onChange={(e) => {
                            const checked = e.currentTarget.checked;
                            setIncluded((prev) => ({ ...prev, [sheet.name]: checked }));
                            if (checked) setActiveSheetIdx(i);
                          }}
                          aria-label={`Import ${sheet.name}`}
                        />
                        Import
                      </label>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <select
              value={activeSheetIdx}
              onChange={(e) => setActiveSheetIdx(Number(e.currentTarget.value))}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200 dark:border-border dark:bg-surface dark:text-slate-100 dark:focus:ring-slate-700"
            >
              {sheets.map((sheet, i) => (
                <option key={sheet.name} value={i}>
                  {sheet.name}
                  {lowConfidence[sheet.name] ? " ⚠" : ""}
                  {" — "}
                  {sheetRowCounts[sheet.name] ?? 0} rows
                </option>
              ))}
            </select>
          )}
          <p className="mt-2 text-[11px] text-slate-400 dark:text-slate-500">
            {pickMode
              ? "Tick every sheet you want. Click a sheet's name to set its header rows and template; unticked sheets are skipped."
              : "Each sheet keeps its own header settings."}
          </p>
        </div>
      )}

      {/* Per-sheet config panel */}
      {activeSheet && currentConfig && (
        <SheetConfigPanel
          key={activeSheet.name}
          sheet={activeSheet}
          config={currentConfig}
          lowConfidence={lowConfidence[activeSheet.name] ?? false}
          onConfig={(next) => handleSheetConfig(activeSheet.name, next)}
          onConfirmed={() => handleSheetConfirmed(activeSheet.name)}
        />
      )}

      {/* Template pick for the sheet on screen */}
      {pickMode && activeSheet && (
        isIncluded(activeSheet.name) ? (
          <div className="flex flex-col gap-3 rounded-[10px] border border-slate-200 bg-slate-50 p-4 dark:border-border dark:bg-surface-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <label
                htmlFor={`template-${activeSheet.name}`}
                className="text-[13px] font-semibold text-slate-900 dark:text-slate-100"
              >
                Template for “{activeSheet.name}”
              </label>
              <p className="mt-0.5 text-[12px] leading-5 text-slate-500 dark:text-slate-400">
                A template is a saved column mapping that turns this sheet into the
                Shopify CSV. Choose “No template” to map the columns yourself.
              </p>
            </div>
            <select
              id={`template-${activeSheet.name}`}
              value={templateBySheet[activeSheet.name] ?? ""}
              onChange={(e) => {
                const value = e.currentTarget.value;
                setTemplateBySheet((prev) => ({ ...prev, [activeSheet.name]: value }));
              }}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200 dark:border-border dark:bg-surface dark:text-slate-100 dark:focus:ring-slate-700 sm:w-72"
            >
              <option value="">No template, map it myself</option>
              {(templates ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className="rounded-[10px] border border-dashed border-slate-300 px-4 py-3 text-[12.5px] text-slate-500 dark:border-border dark:text-slate-400">
            “{activeSheet.name}” won&apos;t be imported. Tick <strong>Import</strong> on
            its card to include it.
          </div>
        )
      )}

      {sheets.length === 0 && (
        <div className="py-12 text-center text-sm text-slate-500 dark:text-slate-400">
          No sheets were found in this file.
        </div>
      )}

      {/* Footer action row */}
      <div className="flex items-center justify-between gap-3 border-t border-slate-200 pt-4 dark:border-border">
        <p className="text-[11px] text-slate-500 dark:text-slate-400">
          {pickMode && sheets.length > 1
            ? `${includedSheets.length} of ${sheets.length} sheets will import.`
            : sheets.length > 1
            ? `${sheets.length} sheets — configure each, then confirm.`
            : sheets.length === 1 && currentConfig
              ? `${sheetRowCounts[activeSheet!.name] ?? 0} rows will import`
              : ""}
        </p>
        <div className="flex items-center gap-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-border dark:bg-surface dark:text-slate-300 dark:hover:bg-surface-2"
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={confirm}
            disabled={busy || includedSheets.length === 0 || !allSheetsReady}
            title={
              includedSheets.length === 0
                ? "Tick at least one sheet to import"
                : !allSheetsReady
                  ? "Please confirm the header row for all sheets with warnings"
                  : undefined
            }
            className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-slate-100 dark:text-slate-950 dark:hover:bg-slate-200"
          >
            {busy && <LoaderCircle className="h-4 w-4 animate-spin" />}
            {label}
          </button>
        </div>
      </div>
    </div>
  );
}
